import {Department} from '@app/core/models/department.model';
import {User} from '@app/core/models/user.model';
import {Group} from '@app/core/models/group.model';
import {RDate} from '@app/core/models/rDate.model';
import {InternationalizationService} from '@app/core/services/internationalization.service';
import {BaseModel} from '@app/core/models/base.model';
import * as moment from 'moment';
import {Moment, unitOfTime} from 'moment';
import {getCurrentState} from '@app/core/helpers';
import {IAppState} from '@app/core/store/state/app.state';
import {Store} from '@ngrx/store';
import {IQOIconsOverlap} from '@quickorder/angular-web-lib/lib/types/interfaces/q-o-icons-overlap.interface';
import {BulletinBoardPostLike} from '@app/core/models/bulletinBoardPostLike.model';

export enum ShiftState {
	NONE,
	CREATING,
	UPDATING,
	REMOVING,
}

export class Shift extends BaseModel {
	public static readonly subjectType: string = 'App\\Models\\Shift';
	public static readonly visibleCreated: string[] = ['user_id', 'group_id', 'start', 'end', 'pause', 'note'];
	public static readonly visibleUpdated: string[] = ['user_id', 'group_id', 'start', 'end', 'pause', 'note', 'punch_note', 'punch_start', 'punch_end', 'approved_start', 'approved_end'];
	public static readonly dates: string[] = ['start', 'end', 'punch_start', 'punch_end', 'approved_start', 'approved_end'];
	public id: number;
	public start: RDate;
	public end: RDate;
	public punch_start: RDate;
	public punch_end: RDate;
	public rounded_punch_start: RDate;
	public rounded_punch_end: RDate;
	public original_start: RDate;
	public original_end: RDate;
	public visible_date: string;
	public punch_not_approved_reasons: number[];
	public department: Department;
	public pause: number;
	public user: User;
	public note: string;
	public group: Group;
	public showing_punch_start: boolean;
	public has_punch_start: boolean;
	public showing_punch_end: boolean;
	public has_punch_end: boolean;
	public public_shift_stock_id: number;
	public approved_start: RDate;
	public approved_end: RDate;
	public bid_on_shift: boolean;
	public bids_on_shift: number;
	public approved: boolean;
	public extra_shift: boolean;
	public amount_of_bids_on_shift: number;
	public punch_note: string;
	public punch_not_approved_reasons_array: number[] = [];
	public blink: boolean;
	public selected: boolean = false;
	public tooltip: string;
	public startTime: string;
	public endTime: string;
	public state: ShiftState = ShiftState.NONE;
	public sick_message_id?: number;
	public users_who_bid?: User[];
	public users_profile_icons: IQOIconsOverlap[] = [];

	public static formatAtt(tenantTZ: string, interService: InternationalizationService, store: Store<IAppState>, key, val): string {
		if (this.dates.indexOf(key) !== -1) {
			return new RDate(tenantTZ, interService).deserialize(val).localDate.format('HH:mm');
		}
		if (key === 'user_id') {
			return getCurrentState(store).activity.users[val] ?
				getCurrentState(store).activity.users[val].getFullName() : interService.translate.instant('ACTIVITY_LOG.UNKNOWN_ENTITY');
		}
		if (key === 'group_id') {
			return getCurrentState(store).activity.groups[val] ?
				getCurrentState(store).activity.groups[val].group_name : interService.translate.instant('ACTIVITY_LOG.UNKNOWN_ENTITY');
		}

		return val;
	}

	public static getTitle(tenantTZ: string, interService: InternationalizationService, activityDescription): string {
		return interService.translate.instant('ACTIVITY_LOG.' + activityDescription) + ' ' + interService.translate.instant('ACTIVITY_LOG.DEFINITE.' + this.subjectType);
	}

	constructor(tenantTZ: string, interService: InternationalizationService) {
		super(tenantTZ, interService);
	}

	public getStart(): RDate {
		if (this.approved_start) {
			return this.approved_start;
		} else {
			return (this.punch_start)
				? this.punch_start
				: this.start;
		}
	}

	public getEnd(): RDate {
		if (this.approved_end) {
			return this.approved_end;
		} else {
			return (this.punch_end)
				? this.punch_end
				: this.end;
		}
	}

	public diff(date: Moment, startTime: string, endTime: string, format: unitOfTime.Diff = 'seconds'): number {
		const startDate = moment(date.format('YYYY-MM-DD') + startTime, 'YYYY-MM-DD HH:mm');
		const endDate = moment(date.format('YYYY-MM-DD') + endTime, 'YYYY-MM-DD HH:mm');
		if (endDate.isBefore(startDate)) {
			endDate.add(1, 'day');
		}

		return endDate.diff(startDate, format);
	}

	public deserialize(input: any): this {
		Object.assign(this, input);
		if (input.start && !(input.start instanceof RDate)) {
			this.start = new RDate(this.tenantTZ, this.interService).deserialize(input.start, 'UTC', undefined, true);
		}
		if (input.end && !(input.end instanceof RDate)) {
			this.end = new RDate(this.tenantTZ, this.interService).deserialize(input.end, 'UTC', undefined, true);
		}
		if (input.punch_start && !(input.punch_start instanceof RDate)) {
			this.punch_start = new RDate(this.tenantTZ, this.interService).deserialize(input.punch_start, 'UTC', undefined, true);
		}
		if (input.punch_end && !(input.punch_end instanceof RDate)) {
			this.punch_end = new RDate(this.tenantTZ, this.interService).deserialize(input.punch_end, 'UTC', undefined, true);
		}
		if (input.original_start && !(input.original_start instanceof RDate)) {
			this.original_start = new RDate(this.tenantTZ, this.interService).deserialize(input.original_start, 'UTC', undefined, true);
		}
		if (input.original_end && !(input.original_end instanceof RDate)) {
			this.original_end = new RDate(this.tenantTZ, this.interService).deserialize(input.original_end, 'UTC', undefined, true);
		}
		if (input.approved_start && !(input.approved_start instanceof RDate)) {
			this.approved_start = new RDate(this.tenantTZ, this.interService).deserialize(input.approved_start, 'UTC', undefined, true);
		}
		if (input.approved_end && !(input.approved_end instanceof RDate)) {
			this.approved_end = new RDate(this.tenantTZ, this.interService).deserialize(input.approved_end, 'UTC', undefined, true);
		}
		if (input.rounded_punch_start) {
			this.rounded_punch_start = new RDate(this.tenantTZ, this.interService).deserialize(input.rounded_punch_start, 'UTC', undefined, true);
		}
		if (input.rounded_punch_end) {
			this.rounded_punch_end = new RDate(this.tenantTZ, this.interService).deserialize(input.rounded_punch_end, 'UTC', undefined, true);
		}
		this.punch_not_approved_reasons = [];
		if (input.punch_not_approved_reasons) {
			const arr = (Array.isArray(input.punch_not_approved_reasons)) ? input.punch_not_approved_reasons : input.punch_not_approved_reasons.split(',');
			arr.forEach((reasonType) => {
				this.punch_not_approved_reasons.push(reasonType);
				reasonType = parseInt(reasonType);
				this.punch_not_approved_reasons_array.push(reasonType);
			});
		}
		if (input.department && !(input.department instanceof Department)) {
			this.department = new Department(this.tenantTZ, this.interService).deserialize(input.department);
		}
		if (input.group && !(input.group instanceof Group)) {
			this.group = new Group(this.tenantTZ, this.interService).deserialize(input.group);
		}
		if (input.user && !(input.user instanceof User)) {
			this.user = new User(this.tenantTZ, this.interService).deserialize(input.user);
		}
		if (input.users_who_bid) {
			this.users_who_bid = input.users_who_bid.map(u => new User(this.tenantTZ, this.interService).deserialize(u));
			this.users_profile_icons = this.constructIcons(this.users_who_bid.slice(0, 5));
		}
		this.pause = parseInt(input.pause);

		return this;
	}

	public constructIcons(users: User[]): IQOIconsOverlap[] {
		return users.map(user => {
			const icon = {imageUrl: undefined, initials: user.getInitials()};
			if (user.profile_picture && user.profile_picture.download_url) {
				icon['imageUrl'] = user.profile_picture.download_url;
			}

			return icon;
		});
	}
}
